// src/utils/itemAvailability.ts

export interface ItemAvailabilityCheck {
  isAvailable: boolean;
  reason: 'available' | 'out_of_stock' | 'not_verified' | 'deleted';
  message: string;
}

/**
 * Check item availability with priority order:
 * 1. Check inventoryStatus (highest priority) - Out of Stock
 * 2. Check isItemVerified - Not Verified
 * 3. Check isDeleted - Item Deleted
 * 
 * Uses camelCase field names from API response
 * 
 * @param item - Item object with availability fields
 * @returns ItemAvailabilityCheck object with availability status and message
 */
export function checkItemAvailability(item: {
  inventoryStatus?: boolean;
  isItemVerified?: boolean;
  isDeleted?: boolean;
}): ItemAvailabilityCheck {
  const inventoryStatus = item.inventoryStatus;
  const isVerified = item.isItemVerified;
  const isDeleted = item.isDeleted;

  // Priority 1: Check inventory status first (Out of Stock)
  if (inventoryStatus === false) {
    return {
      isAvailable: false,
      reason: 'out_of_stock',
      message: 'Out of Stock'
    };
  }

  // Priority 2: Check if item is verified
  if (isVerified === false) {
    return {
      isAvailable: false,
      reason: 'not_verified',
      message: 'Unavailable'
    };
  }

  // Priority 3: Check if item is deleted
  if (isDeleted === true) {
    return {
      isAvailable: false,
      reason: 'deleted',
      message: 'Item Unavailable'
    };
  }

  // All checks passed - item is available
  return {
    isAvailable: true,
    reason: 'available',
    message: 'Add to Cart'
  };
}

/**
 * Filter menu items to only show available ones
 * Filters out items that are:
 * - Out of stock (inventoryStatus === false)
 * - Not verified (isItemVerified === false)
 * - Deleted (isDeleted === true)
 * 
 * Uses camelCase field names from API response
 * 
 * @param items - Array of menu items to filter
 * @returns Filtered array of available items only
 */
export function filterAvailableItems<T extends {
  inventoryStatus?: boolean;
  isItemVerified?: boolean;
  isDeleted?: boolean;
  [key: string]: any; // Allow additional properties
}>(items: T[]): T[] {
  return items.filter(item => {
    const availability = checkItemAvailability({
      inventoryStatus: item.inventoryStatus,
      isItemVerified: item.isItemVerified,
      isDeleted: item.isDeleted
    });
    
    return availability.isAvailable;
  });
}
